import type { Permission_index } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import jwtDecode from 'jwt-decode';
import type { Adapter } from '@frontend/shell-utils';
import { debounce } from '@frontend/timer';
import type { DecodedToken } from '../types/token.types';
import type { CoreACLs } from '../types/weave.types';
import { BrowserTokenStorageStrategy } from './BrowserTokenStorageStrategy';
import { ElectronTokenStorageStrategy } from './ElectronTokenStorageStrategy';
import type { TokenStorageStrategy } from './TokenStorageStrategy';

type TokenChangeCallback = (token: string | undefined, jti: string | undefined) => void;

/**
 * Don't create TokenStorage instances directly; use the getTokenStorage singleton.
 *
 * Manages the authentication token, including caching and cross-tab synchronization.
 * Encapsulates a TokenStorageStrategy which defines how the token is persisted (e.g. in localStorage or in Electron safeStorage).
 * The default strategy is BrowserTokenStorageStrategy.
 */
export class TokenStorage {
  private cachedToken: string | undefined;
  private cachedJTI: string | undefined;
  private cachedDecodedToken: DecodedToken<CoreACLs | Permission_index> | undefined;
  private cachedTokenReceivedAt: number | null | undefined; // null = loaded but strategy had no value
  private tokenChangeCallbacks: TokenChangeCallback[] = [];
  private unsubscribeStorageChange: (() => void) | undefined;

  constructor(private strategy: TokenStorageStrategy) {}

  /** use for debugging or testing, not for branching logic */
  getTokenStorageStrategyName(): string {
    return this.strategy.name;
  }

  getToken(): string | undefined {
    if (this.cachedToken) {
      return this.cachedToken;
    }
    this.cachedToken = this.strategy.getToken();
    return this.cachedToken;
  }

  setToken(token: string, jti: string): void {
    const now = Date.now();
    this.strategy.setToken(token);
    this.strategy.setJTI(jti);
    this.strategy.setTokenReceivedAt(now);
    this.cachedJTI = jti;
    this.cachedToken = token;
    this.cachedDecodedToken = undefined;
    this.cachedTokenReceivedAt = now;
    this.invokeTokenChangeCallbacks(token, jti);
  }

  getJTI(): string | undefined {
    if (this.cachedJTI) {
      return this.cachedJTI;
    }
    this.cachedJTI = this.strategy.getJTI();
    return this.cachedJTI;
  }

  getTokenReceivedAt(fallback: number): number {
    if (this.cachedTokenReceivedAt !== undefined) {
      return this.cachedTokenReceivedAt ?? fallback;
    }
    const stored = this.strategy.getTokenReceivedAt();
    this.cachedTokenReceivedAt = stored ?? null;
    return stored ?? fallback;
  }

  getDecodedToken<A extends CoreACLs | Permission_index = CoreACLs>(): DecodedToken<A> | undefined {
    const stored = this.cachedDecodedToken as DecodedToken<A>;
    if (stored) {
      return stored;
    }
    const token = this.getToken();
    if (token) {
      try {
        const decoded = jwtDecode<DecodedToken<A>>(token);
        this.cachedDecodedToken = decoded as DecodedToken<CoreACLs | Permission_index>;
        return decoded;
      } catch {
        return undefined;
      }
    }
    return undefined;
  }

  invalidateDecodedToken(): void {
    this.cachedDecodedToken = undefined;
  }

  clearCache(): void {
    this.cachedToken = undefined;
    this.cachedJTI = undefined;
    this.cachedDecodedToken = undefined;
    this.cachedTokenReceivedAt = undefined;
  }

  clearStoredAuthData(): void {
    this.strategy.clearStoredAuthData();
    this.clearCache();
  }

  onTokenChange(cb: TokenChangeCallback): () => void {
    this.tokenChangeCallbacks.push(cb);
    return () => {
      this.tokenChangeCallbacks = this.tokenChangeCallbacks.filter((c) => c !== cb);
    };
  }

  // When the underlying strategy's storage changes (e.g. another tab updates localStorage, or in Electron the main process updates safeStorage),
  // update our cache and notify subscribers.
  registerStorageEventListener(): () => void {
    // debounced to coalesce multiple changes from a single update -- localStorage events all come in separately for token, jti, and tokenReceivedAt, whereas Electron sends them together
    const debouncedInvokeCallbacks = debounce(() => {
      this.invokeTokenChangeCallbacks(this.getToken(), this.getJTI());
    }, 10);

    this.unsubscribeStorageChange = this.strategy.onStorageChange((field, newValue) => {
      switch (field) {
        case 'token': {
          const tokenChanged = newValue != null && newValue !== this.cachedToken;
          this.cachedToken = undefined;
          this.cachedDecodedToken = undefined;
          this.cachedTokenReceivedAt = undefined;
          if (tokenChanged) {
            debouncedInvokeCallbacks();
          }
          break;
        }
        case 'jti': {
          const jtiChanged = newValue != null && newValue !== this.cachedJTI;
          this.cachedJTI = undefined;
          this.cachedTokenReceivedAt = undefined;
          if (jtiChanged) {
            debouncedInvokeCallbacks();
          }
          break;
        }
      }
    });

    return () => {
      this.unsubscribeStorageChange?.();
      this.unsubscribeStorageChange = undefined;
    };
  }

  private invokeTokenChangeCallbacks(token: string | undefined, jti: string | undefined): void {
    this.tokenChangeCallbacks.forEach((cb) => cb(token, jti));
  }
}

// Singleton instance and accessor for the TokenStorage
let instance: TokenStorage | undefined;

function shellIsAvailable(shell: Adapter | undefined): shell is Adapter {
  if (!shell) {
    return false;
  }
  return shell?.isShell === true && typeof shell.invokeSync === 'function';
}

export function getTokenStorage(): TokenStorage {
  if (!instance) {
    const shell = typeof window !== 'undefined' ? window.shell : undefined;
    let strategy: TokenStorageStrategy;

    // NOTE desktop-browser-auth is gated behind a flag in shell
    // just because you're shell doesn't mean you're always getting ElectronTokenStorageStrategy (yet)
    if (shellIsAvailable(shell) && shell.featureAvailability?.has('desktop-browser-auth')) {
      strategy = new ElectronTokenStorageStrategy(shell);
      console.log('TokenStorage: using ElectronTokenStorageStrategy'); // temporary log to verify we're getting the expected strategy in shell
    } else {
      strategy = new BrowserTokenStorageStrategy();
    }

    strategy.init();

    instance = new TokenStorage(strategy);
  }
  return instance;
}

/** Reset the singleton — for testing only. */
export function resetTokenStorage_testOnly(): void {
  instance = undefined;
}
